import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import type {
  IEntityDataItem,
  TEntityId,
  TEntityVersion,
} from '~/types/models'

/**
 * Optimistic-concurrency enforcement for the mock API.
 *
 * `mockDataCommon` carried the same comment in two places for years — "On real
 * server, should compare submitted version to currently stored version and
 * reject update if different" — and then did the opposite: it bumped the version
 * and returned 200 to every write, whatever version arrived.
 *
 * That single gap made an entire class of defect invisible to us. Dev, the e2e
 * suite and the unit suite are all mock-backed, so a stale-version 409 could
 * only ever be discovered in production — which is exactly what happened with
 * the lastSelectedState conflicts (see services/errors.ts UpdateConflictError).
 * Closing it is what makes that class reproducible before it ships.
 *
 * ⚠️ The precondition is LENIENT: a write that submits no version, or targets a
 * row that has none, is accepted. That is the standard If-Match / merge-patch
 * reading, and it is a deliberate ASSUMPTION rather than a known fact — the real
 * API's version contract is not documented anywhere in this repo. Being strict
 * instead would reject a large number of existing mock flows that never send a
 * version, which would say more about the mock than about the server.
 */
export function doEnforceMockVersionConflicts(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return getPublicRuntimeConfig().mockEnforceVersions !== false
}

/**
 * Is the submitted version out of date with respect to what is stored?
 *
 * Compared as STRINGS on purpose. `TEntityVersion` is a version *string* in the
 * types, but the mock's own `getNextVersionNumberString` produces one by
 * incrementing a number, and mock fixtures are inconsistent about which they
 * hold — so a `3` from a fixture and a `'3'` from a round-trip must not read as
 * a conflict.
 */
export function isStaleVersion(
  submittedVersion: TEntityVersion | undefined | null,
  storedVersion: TEntityVersion | undefined | null
): boolean {
  if (submittedVersion === undefined || submittedVersion === null) {
    return false
  }
  if (storedVersion === undefined || storedVersion === null) {
    return false
  }
  return String(submittedVersion) !== String(storedVersion)
}

/**
 * The 409 body. Shaped to be USEFUL rather than to match the real API byte for
 * byte — its shape is unknown, and `UpdateConflictError` carries whatever
 * arrives through to Honeybadger without depending on the fields.
 */
export function versionConflictBody({
  id,
  submittedVersion,
  storedVersion,
}: {
  id: TEntityId | undefined
  submittedVersion: TEntityVersion | undefined | null
  storedVersion: TEntityVersion | undefined | null
}) {
  return {
    statusCode: 409,
    message:
      `Version conflict on '${id}': submitted version` +
      ` '${submittedVersion}' is not the stored version '${storedVersion}'.`,
    id,
    submittedVersion,
    storedVersion,
  }
}

/**
 * The first stale row in a submitted batch, or `undefined` if there is none.
 *
 * ⚠ THE `doEnforceMockVersionConflicts()` GATE IS INSIDE THIS FUNCTION, ON
 * PURPOSE. It used to sit in the caller, and the caller is exactly what got
 * forgotten: the three msService collection-PATCH handlers each grew an
 * `Idempotency-Key` branch that calls
 * `updateEntityListDataMultipleFromUpdatedEntityData` directly, skipping the
 * wrapper that held the check — and since `update*MSService` always sets
 * `doAsync: true` for an array, that branch is the ONLY one those requests ever
 * take. Every multi-row msService PATCH in the mock accepted any version at all,
 * silently, for as long as the async branch has existed. A gate a caller can
 * omit is a gate that will be omitted.
 *
 * Returns the row's identity plus its `index` in `submittedRows`, so a caller
 * writing a batch of batches can say WHERE it failed.
 *
 * The staleness rule itself is `isStaleVersion` above and is deliberately
 * lenient — no submitted version, or no stored version, is not a conflict.
 */
export function findVersionConflict({
  listData,
  submittedRows,
}: {
  listData: IEntityDataItem[]
  submittedRows: Partial<IEntityDataItem>[]
}):
  | {
      id: TEntityId | undefined
      submittedVersion: TEntityVersion | undefined
      storedVersion: TEntityVersion | undefined
      index: number
    }
  | undefined {
  if (!doEnforceMockVersionConflicts()) {
    return undefined
  }
  for (const [index, submitted] of submittedRows.entries()) {
    const stored = listData.find((item) => item.id === submitted.id)
    if (stored && isStaleVersion(submitted.version, stored.version)) {
      return {
        id: submitted.id,
        submittedVersion: submitted.version,
        storedVersion: stored.version,
        index,
      }
    }
  }
  return undefined
}
